import type {
  CreateTestSessionErrorCode,
  CreateTestSessionResponse,
  TestSessionType,
} from '../api/membersApi';

/** The synthetic test session is always 50 minutes; only the start is chosen (MEN-4943). */
export const TEST_SESSION_DURATION_MINUTES = 50;

/**
 * MEN-5030: production test sessions are only allowed for members in this email
 * domain (MEN-5455 relaxes it elsewhere). The backend enforces it; this mirror
 * only gives the admin an early explanation.
 */
export const INTERNAL_MEMBER_DOMAIN = 'mento.co';

export const NON_INTERNAL_MEMBER_MESSAGE = `Test sessions can only be created for members with a @${INTERNAL_MEMBER_DOMAIN} email address.`;

export const TEST_SESSION_TYPE_OPTIONS: ReadonlyArray<{ value: TestSessionType; label: string }> = [
  { value: 'coaching_session', label: 'Coaching Session' },
  { value: 'chem_call', label: 'Chemistry Call' },
];

/** Mirrors the server rule: exactly the internal domain, case-insensitive, no subdomains. */
export function isInternalMemberEmail(email: string): boolean {
  const address = email.trim().toLowerCase();
  const at = address.lastIndexOf('@');
  if (at < 0) {
    return false;
  }
  return address.slice(at + 1) === INTERNAL_MEMBER_DOMAIN;
}

function pad(value: number): string {
  return String(value).padStart(2, '0');
}

/**
 * Formats a Date as an RFC 3339 timestamp carrying the browser's UTC offset,
 * with seconds fixed at :00 so the server sees an exact minute.
 */
export function formatOffsetIsoMinute(date: Date): string {
  const offsetMinutes = -date.getTimezoneOffset();
  const sign = offsetMinutes >= 0 ? '+' : '-';
  const absolute = Math.abs(offsetMinutes);
  const offset = `${sign}${pad(Math.floor(absolute / 60))}:${pad(absolute % 60)}`;
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}:00${offset}`;
}

/**
 * Parses a `datetime-local` input value (interpreted in the browser's zone, as the
 * input itself does) into a Date at that exact minute, or null when unparseable.
 */
export function parseLocalDateTimeMinute(value: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(value.trim());
  if (!match) {
    return null;
  }
  const [, year, month, day, hour, minute] = match;
  const date = new Date(
    Number(year),
    Number(month) - 1,
    Number(day),
    Number(hour),
    Number(minute),
    0,
    0
  );
  if (
    Number.isNaN(date.getTime()) ||
    date.getFullYear() !== Number(year) ||
    date.getMonth() !== Number(month) - 1 ||
    date.getDate() !== Number(day) ||
    date.getHours() !== Number(hour) ||
    date.getMinutes() !== Number(minute)
  ) {
    return null;
  }
  return date;
}

export function browserTimeZoneLabel(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'local time';
  } catch {
    return 'local time';
  }
}

/** Mirrors the server rule: start + 50 minutes must not be in the future. */
export function sessionHasEnded(startedAt: Date, now: Date = new Date()): boolean {
  return startedAt.getTime() + TEST_SESSION_DURATION_MINUTES * 60_000 <= now.getTime();
}

export function sessionIsFuture(startedAt: Date, now: Date = new Date()): boolean {
  return startedAt.getTime() > now.getTime();
}

export function buildTestSessionCreationMessage(result: CreateTestSessionResponse): string {
  if (result.fixture_kind === 'scheduled') {
    return result.created
      ? 'Scheduled test session created.'
      : 'Scheduled test session already existed.';
  }
  const generation = result.generation!;
  const counts = [
    `${generation.jobs_enqueued} queued`,
    `${generation.already_queued} already queued`,
    `${generation.already_satisfied} already satisfied`,
    `${generation.skipped_disabled} skipped`,
    `${generation.blocked_targets} blocked`,
  ].join(', ');
  const prefix = result.created
    ? 'Test session created and ready.'
    : 'Test session already existed and is ready.';
  const message = `${prefix} Artifact jobs: ${counts}.`;
  if (result.session_type === 'chem_call') {
    // Chem Call reflection is queued by the normal ready-transition workflow, not
    // by the inline catch-up these counts come from (MEN-5030 decision 4).
    return `${message} The Chem Call reflection queues through the normal ready workflow and is not included in these counts.`;
  }
  return message;
}

export interface TestSessionApiError {
  code?: CreateTestSessionErrorCode | string;
  description?: string;
}

export function extractTestSessionApiError(error: unknown): TestSessionApiError {
  if (!error || typeof error !== 'object') {
    return {};
  }
  const data = 'data' in error ? (error as { data?: unknown }).data : undefined;
  if (!data || typeof data !== 'object') {
    return {};
  }
  const typed = data as { error?: unknown; error_description?: unknown };
  return {
    code: typeof typed.error === 'string' ? typed.error : undefined,
    description: typeof typed.error_description === 'string' ? typed.error_description : undefined,
  };
}

/**
 * Admin-facing copy per stable error code. Retryable failures say so explicitly
 * because the same form values replay the same fixture.
 */
export function describeTestSessionError(error: unknown): string {
  const { code, description } = extractTestSessionApiError(error);
  switch (code) {
    case 'transcript_conflict':
      return 'A test session for this member, coach, and time already exists with different transcript text. Pick a different date/time or paste the original transcript.';
    case 'unusable_transcript':
      return 'This transcript would be rejected by readiness: it needs at least about a minute of conversation (around 100 words of speaker dialogue, e.g. "Coach: ..." lines, or of transcript text). Gemini notes alone are not enough.';
    case 'real_history_acknowledgement_required':
      return 'You must acknowledge that this creates real completed coaching history.';
    case 'current_coach_not_found':
      return `The selected coach is not a current coach of this member in both systems. ${description ?? ''}`.trim();
    case 'member_not_internal':
      return `${NON_INTERNAL_MEMBER_MESSAGE} Nothing was created.`;
    case 'fixture_conflict':
      return `This test session id already belongs to different data (the same member, coach, and start time with the other session type counts). Pick a different date/time. ${description ?? ''}`.trim();
    case 'projection_unavailable':
      return 'Rails did not finish creating or projecting the session. Retry with the same values; the same test session will be completed.';
    case 'projection_failed':
      return 'Rails created the session but could not project it. Retrying will not fix this fixture; ask engineering for help.';
    case 'fixture_incomplete':
      return 'The test session was not fully completed. Retry with the same values; the same test session will be completed.';
    case 'invalid_request':
      return description || 'The request was invalid.';
    default:
      return description || 'Failed to create the test session. Retry with the same values.';
  }
}
